import type { JSX } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthProvider.js';
import { AppShell } from './layout/AppShell.js';
import { LoginPage } from './pages/LoginPage.js';
import { OverviewPage } from './pages/OverviewPage.js';
import { UsersPage } from './pages/UsersPage.js';
import { CirclesPage } from './pages/CirclesPage.js';
import { ConsentsPage } from './pages/ConsentsPage.js';
import { PlansPage } from './pages/PlansPage.js';
import { BillingPage } from './pages/BillingPage.js';
import { JobsPage } from './pages/JobsPage.js';
import { AuditPage } from './pages/AuditPage.js';
import { AiPage } from './pages/AiPage.js';
import { ContentPage } from './pages/ContentPage.js';
import { NotificationsPage } from './pages/NotificationsPage.js';
import { PrivacyPage } from './pages/PrivacyPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { SecurityPage } from './pages/SecurityPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';

function Protected({ children }: { children: JSX.Element }): JSX.Element {
  const { session, loading, error } = useAuth();
  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <CircularProgress aria-label="Carregando sessão" />
      </Box>
    );
  }
  if (!session) {
    return (
      <Box sx={{ maxWidth: 520, mx: 'auto', mt: 8, px: 2 }}>
        {error ? <Alert severity="error">{error}</Alert> : null}
        <LoginPage />
      </Box>
    );
  }
  return children;
}

export function App(): JSX.Element {
  return (
    <AuthProvider>
      <Routes>
        <Route
          path="/login"
          element={
            <Box sx={{ maxWidth: 520, mx: 'auto', mt: 8, px: 2 }}>
              <LoginPage />
            </Box>
          }
        />
        <Route
          path="/"
          element={
            <Protected>
              <AppShell />
            </Protected>
          }
        >
          <Route index element={<Navigate to="/overview" replace />} />
          <Route path="overview" element={<OverviewPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="circles" element={<CirclesPage />} />
          <Route path="consents" element={<ConsentsPage />} />
          <Route path="plans" element={<PlansPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="jobs" element={<JobsPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="ai" element={<AiPage />} />
          <Route path="content" element={<ContentPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
}